// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { THEME_PREFERENCES } from '../theme';
import type { ThemePreference } from '../theme';
import { SegmentedControl } from './SegmentedControl';

const LABELS: Record<ThemePreference, string> = {
  light: 'Lys',
  dark: 'Mørk',
  system: 'System',
};

/**
 * Icons drawn with `currentColor`; SegmentedControl sizes them, so they
 * inherit the segment's text colour in both themes without any override.
 */
function ThemeIcon({ preference }: { preference: ThemePreference }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (preference) {
    case 'light':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      );
    case 'dark':
      return (
        <svg {...common}>
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      );
    case 'system':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="12" rx="1" />
          <path d="M8 20h8M12 16v4" />
        </svg>
      );
  }
}

const ITEMS = THEME_PREFERENCES.map((option) => ({
  value: option,
  label: `${LABELS[option]} tema`,
  icon: <ThemeIcon preference={option} />,
}));

/**
 * Three-way Light / Dark / System theme control: the shared SegmentedControl,
 * icon-only at bar size (CasesBar, Modelindstillinger, Login, the share page).
 *
 * A single-select radio group: the checked segment is the stored *preference*
 * (which may be `system`), not the resolved theme. Selecting one persists it
 * and repaints the whole app via the `data-theme` attribute — no per-component
 * work, since every style already reads `var(--…)` tokens.
 *
 * Presentational: the shell calls `useTheme()` once and hands the same
 * `preference`/`setPreference` to every instance, so they can never disagree
 * about which option is checked.
 */
export interface ThemeToggleProps {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** 44px options, toned for the navy chrome. */
  touch?: boolean;
}

export function ThemeToggle({ preference, setPreference, touch = false }: ThemeToggleProps) {
  return (
    <SegmentedControl
      aria-label="Farvetema"
      items={ITEMS}
      value={preference}
      onChange={setPreference}
      iconOnly
      size="md"
      touch={touch}
      onChrome={touch}
    />
  );
}
